import {
  Id,
  Identifiable,
  Project,
  resource as resourceMock,
  list as listMock,
  task as taskMock,
  TimeSheet,
  TimeSheetFilter,
  Resource,
  project as projectMock,
  ProjectElementType,
  ResourceTypes,
  OptimizedTimeSheet,
  allocation,
} from "@planzone/dto";
import { HttpClient, HttpMethods } from "../types/httpClient";

type ResponseList = {
  uuid: Id;
  name: string;
  nbrComments: number;
  spentTimes: { [key: string]: number };
  nbrAllocatedHours: number;
  totalOfSpentTime: number;
  totalOfSpentTimeInWeek: number;
  totalOfRemainingTime: number;
  percentComplete: number;
  subActivities: ResponseList[];
  tasks: {
    uuid: Id;
    name: string;
    spentTimes: { [key: string]: number };
    nbrAllocatedHours: number;
    totalOfSpentTime: number;
    totalOfSpentTimeInWeek: number;
    totalOfRemainingTime: number;
    keyRank: number;
    timeRecords: {
      uuid: Id;
      date: number;
      month: number;
      year: number;
      timeSpent: number;
      attributes?: { [key: string]: string };
      timeSpentInWeek: number;
    }[];
    taskResourceLatestRecord: string;
    taskResourceLatestRecordingDay: string;
    resourcePercentComplete: number;
    allocationData: {
      allocationUuid: string;
      resourceUuid: string;
      percentComplete: number;
      allocatedHours: number;
    }[];
  }[];
};

type Response = {
  resourceUuid: string;
  resourceName: string;
  profession: string;
  type: ResourceTypes;
  deactivated: boolean;
  projectUuid: string;
  projectName: string;
  integratedAt: string;
  isResourceInProject: boolean;
  totalOfAllocatedHours: number;
  totalOfSpentTime: number;
  totalOfSpentTimeInWeek: number;
  totalOfSpentTimeUntilEndDate: number;
  totalOfRemainingTime: number;
  percentCompletion: number;
  spentTimes: { [key: string]: number };
  unavailabilities: { [key: string]: boolean };

  elements: (
    | {
        type: ProjectElementType.LIST;
        uuid: string;
        name: string;
        depth: number;
        keyRank: number;
        parentUuid: string;
        spentTimes: { [key: string]: number };
        totalOfAllocatedHours: number;
        totalOfSpentTime: number;
        totalOfSpentTimeInWeek: number;
        totalOfSpentTimeUntilEndDate: number;
        totalOfRemainingTime: number;
        percentComplete: number;
        timeRecords: null;
        resourceHaveAllocation: null;
        taskResourceLatestRecordingDay: null;
        taskResourceLatestRecord: null;
        allocationData: null;
        endDate: string | null;
        startDate: string | null;
        attributes: null;
        resourcePercentComplete: number | null;
        nbrComments: number;
      }
    | {
        type: ProjectElementType.TASK;
        uuid: string;
        name: string;
        depth: number;
        keyRank: number;
        parentUuid: string;
        spentTimes: { [key: string]: number };
        totalOfAllocatedHours: number;
        totalOfSpentTime: number;
        totalOfSpentTimeInWeek: number;
        totalOfSpentTimeUntilEndDate: number;
        totalOfRemainingTime: number;
        percentComplete: number;
        timeRecords: {
          uuid: string;
          taskUuid: string;
          date: number;
          month: number;
          year: number;
          timeSpent: number;
          attributes?: { [key: string]: string };
          resourceHaveAllocation: boolean;
        }[];
        resourceHaveAllocation: boolean;
        taskResourceLatestRecordingDay: null | string;
        taskResourceLatestRecord: null | string;
        allocationData: {
          uuid: string;
          resourceUuid: string;
          percentComplete: number;
          allocatedHours: number;
        }[];
        endDate: string | null;
        startDate: string | null;
        attributes: { [key: string]: string };
        resourcePercentComplete: number;
        nbrComments: null | number;
      }
  )[];
};

function isUnAvailable(
  at: string,
  unAvailabilities: { [key: string]: boolean },
): boolean {
  return unAvailabilities[at];
}

function responseToSpentTimes(
  res: { [key: string]: number },
  unAvailabilities: { [key: string]: boolean },
): { date: string; time: number; isAvailable: boolean }[] {
  return Object.entries(res)
    .map(([date, time]) => ({
      date,
      time,
      isAvailable: !isUnAvailable(date, unAvailabilities),
    }))
    .sort((first, second) => first.date.localeCompare(second.date));
}

function formatList(
  list: ResponseList,
  project: Identifiable<Project>,
  resource: Identifiable<Resource> & {
    unavailabilities: { [key: string]: boolean };
  },
): TimeSheet["resources"][number]["lists"][number] {
  return {
    ...listMock,
    ...list,
    percentComplete: list.percentComplete,
    project: { ...projectMock, ...project },
    subLists: list.subActivities.map((subList) =>
      formatList(subList, project, resource),
    ),
    spentTimes: responseToSpentTimes(
      list.spentTimes,
      resource.unavailabilities,
    ),
    tasks: list.tasks.map((task) => ({
      ...taskMock,
      ...task,
      activityKeyRank: task.keyRank,
      allocations: task.allocationData.map((allocation) => ({
        uuid: allocation.allocationUuid,
        allocatedHours: allocation.allocatedHours,
        percentComplete: allocation.percentComplete,
        estimatedAt: 0,
        completedAt: "",
        resource: { ...resource, uuid: allocation.resourceUuid },
      })),
      spentTimes: responseToSpentTimes(
        task.spentTimes,
        resource.unavailabilities,
      ),
      timeRecords: task.timeRecords.map((record) => ({
        ...record,
        task: { ...taskMock, ...task },
        resource,
      })),
    })),
  };
}

function responseToTimeSheet(
  res: Response,
  project: Identifiable<Project>,
): OptimizedTimeSheet {
  const resource = {
    ...resourceMock,
    ...res,
    uuid: res.resourceUuid,
    name: res.resourceName,
  };

  return {
    ...res,
    resource,
    project: {
      ...projectMock,
      ...res,
      uuid: res.projectUuid,
      name: res.projectName,
    },
    isResourceInProject: res.isResourceInProject,
    spentTimes: responseToSpentTimes(res.spentTimes, res.unavailabilities),
    elements: res.elements.map((element) => ({
      ...element,
      activityKeyRank: element.keyRank,
      allocations: element.allocationData?.map((data) => ({
        ...allocation,
        ...data,
      })),
      timeRecords:
        element.timeRecords !== null
          ? element.timeRecords.map((record) => ({
              ...record,
              task: {
                ...taskMock,
                ...element,
                startDate: element.startDate || "",
                endDate: element.endDate || "",
                allocations: element.allocationData.map((data) => ({
                  ...allocation,
                  ...data,
                })),
              },
              resource,
            }))
          : [],
      startDate: element.startDate || "",
      endDate: element.endDate || "",
      attributes: element.attributes || {},
      spentTimes: responseToSpentTimes(
        element.spentTimes,
        res.unavailabilities,
      ),
    })),
  };
}

function getOptimizedTimeSheet(httpClient: HttpClient) {
  return (
    resource: Identifiable<Resource>,
    project: Identifiable<Project>,
    { minDate, maxDate, ...filter }: TimeSheetFilter,
    page: number,
    size: number,
  ) =>
    httpClient
      .sendHttpRequest<Response>({
        endpoint: "/timesheets/resources/paginated",
        method: HttpMethods.POST,
        data: {
          resourceUuid: resource.uuid,
          projectUuid: project.uuid,
          startDate: minDate,
          endDate: maxDate,
          pageNumber: size,
          pageSize: page,
          includeCompletedTasks: filter.includeCompletedTasks,
          ...(filter.integrationScope !== undefined && {
            integrationScope: filter.integrationScope,
          }),
        },
      })
      .then((timesheet) => responseToTimeSheet(timesheet, project));
}

export { getOptimizedTimeSheet };
